import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useCSS } from '../../lib/hooks.js';
import { injectIntl } from 'react-intl';
import { i18nIntl, parseClasses, generateUUID } from '../../lib/ep.js';
import { isEqual as _isEqual } from 'lodash';
import { Link } from 'react-router-dom';

import { SquaredButton } from './ActionButton';
import ActionLink from './ActionLink';
import FloatingSelectorList from './FloatingSelectorList';
import ToggleSectionButton from './ToggleSectionButton';

const margin_sides = 10;
const scroll_delta = 200;

const MenuItem = ({e}) => (
	<li>
		<ActionLink _class={{icon:e.icon}} onClick={e.action}>{e.label}</ActionLink>
	</li>
);

export const NavigationTabsBuilder = (props) => {
	let element = document.getElementById('nav_tabs');

	if(element == null) {
		return (
			<NavigationTabs {...props} />
		);
	}

	return ReactDOM.createPortal(
		<NavigationTabs {...props} />,
		element,
	);
};

const NavigationTabs = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const sections = props?.sections ?? [];
	const disable_section = props?.disable_section ?? false;
	const relative_selection = props?.relative_selection ?? false;
	const add_dom_separators = props?.add_dom_separators ?? false;
	const in_frontend = props?.in_frontend ?? false;

	// Use this in case no sections are present, but still wanted to have the NavigationTabs visible
	// because maybe a bunch of ActionButtons or Selects or so instead of usual tabs
	const force_show = props?.force_show ?? false;

	if(sections.length == 0 && !disable_section && !force_show) return null;

	useCSS('comp/tabs.css');

	const [ display_next_section, setDisplayNextSection ] = useState(false);
	const [ display_prev_section, setDisplayPrevSection ] = useState(false);

	const container_w = useRef(0);
	const tabs_w = useRef(0);
	const w_diff = useRef(0);

	const rand_id = useRef(generateUUID());

	const isPaginatorNeeded = () => {
		container_w.current = document.querySelector(`#${rand_id.current} .tabs`)?.offsetWidth;
		tabs_w.current = document.querySelector(`#${rand_id.current} .tabbed_main_actions`)?.offsetWidth;
		w_diff.current = Math.max(tabs_w.current - container_w.current, 0);

		setDisplayPrevSection((display_prev_section && w_diff.current > 0) ? true : false);
		setDisplayNextSection((w_diff.current > 0) ? true : false);

		goSelectedSection();
	};

	useEffect(() => {
		if(!_isEqual(props.sections, sections)) isPaginatorNeeded();
	}, [props.sections]);

	useEffect(() => {
		setTimeout(() => {
			isPaginatorNeeded();
		}, 15);

		window.addEventListener("resize", isPaginatorNeeded);

		return () => {
			window.removeEventListener("resize", isPaginatorNeeded);
		}
	}, []);

	useEffect(() => {
		isPaginatorNeeded();
	}, [(props?.sections ?? []).length]);

	const goSection = (direction) => {
		if(display_next_section || display_prev_section) {
			const item = document.querySelector(`#${rand_id.current} .tabs`);

			const actual_scroll_left = item.scrollLeft;
			const scroll_with_delta = actual_scroll_left + scroll_delta * direction;

			let scroll_to = Math.max(margin_sides, Math.min(w_diff.current + 2 * margin_sides, scroll_with_delta));
			if(scroll_to == margin_sides) scroll_to = 0;
			
			item.scrollLeft = scroll_to;
			setDisplayPrevSection((scroll_to > margin_sides) ? true : false);
			setDisplayNextSection((w_diff.current > scroll_to) ? true : false);
		}
	};

	const goSelectedSection = () => {
		if(display_next_section || display_prev_section) {
			let selected_tab = document.querySelector(`#${rand_id.current} .side_button.selected`);
			if(selected_tab == undefined) selected_tab = {};

			let scroll_to = Math.max(margin_sides, (selected_tab?.offsetLeft ?? 0)) + 15;
			if(scroll_to == margin_sides) scroll_to = 0;
			
			setTimeout(() => {
				document.querySelector(`#${rand_id.current} .tabs`).scrollLeft = scroll_to;
	
				setDisplayPrevSection((scroll_to > margin_sides) ? true : false);
				if(display_prev_section) scroll_to = Math.max(scroll_to, 25);
				setDisplayNextSection((w_diff.current > scroll_to) ? true : false);
			}, 0);
		}
	};

	const removeTab = (onRemoveTab) => {
		if(onRemoveTab != undefined) onRemoveTab();
	};

	let classname = props?.classname ?? 'navigation_tabs_with_sliders';
	classname = parseClasses(classname, props._class, ((in_frontend) ? 'frontend_tabs' : 'tabs'));
	if(in_frontend) classname += ' in_frontend';

	let navigations = 0;
	if(display_prev_section && display_next_section) {
		navigations = 2;
		classname += ' has_both_navigations'
	} else {
		if(display_prev_section) {
			navigations++;
			classname += ' has_prev_navigation'
		}

		if(display_next_section) {
			navigations++;
			classname += ' has_next_navigation'
		}
	}
	if(navigations > 0) classname += ' has_navigation';

	const display_type = props?.display_type ?? 'box';
	const active = props?.active ?? '';

	return (
		<div id={rand_id.current} class={classname} style={props.style}>
			<div class="tabs">
				<ul class="tabbed_main_actions" style={{marginTop:'0'}}>
					{(add_dom_separators) && (
						<li class="separators_ext" />
					)}

					{(disable_section) && (
						<li id="section_tab_0" class="section_toggler">
							<ToggleSectionButton
								_class={props.toggler_button_class}
								value={disable_section.value}
								onClick={(e) => disable_section.toggle()}
								label_on={props.label_on}
								label_off={props.label_off}
								tooltip={props.toggler_tooltip}
								onClickInactive={props.onClickInactive}
								inactive={props.inactive}
								disabled={disable_section.disabled}
							/>
						</li>
					)}

					{sections.map((s, i) => {
						const enabled = s?.enabled ?? true;
						if(!enabled) return null;

						const new_i = (disable_section) ? i + 1 : i;
						const _link = (s.link != undefined || s.full_link != undefined) ? ((s.full_link != undefined) ? s.full_link : s.link) : false;
						const _key = s?.key ?? false;

						let tab_classes = ['side_button', 'tabbed_section', `as_${display_type}`];
						if(s.removable != undefined && s.removable) tab_classes.push('removable');
						if(s.icon == undefined || s.icon == false) tab_classes.push('without_icon');
						if(s.disabled != undefined && s.disabled) tab_classes.push('disabled_tab overflowed_visible');
						const extra_classes = (s.tab_class != undefined) ? s.tab_class : false;
						
						const link = (s.link != undefined || s.full_link != undefined) ? ((s.full_link != undefined) ? s.full_link : `#${s.link}`) : false;

						let tab_item_classes = ['submit', 'submit_link'];
						if(s.icon != undefined) tab_item_classes.push(s.icon);
						if(extra_classes) tab_item_classes.push(extra_classes);

						if((_link !== false && ((relative_selection && _link.indexOf(active) > -1) || (active == `/${_link}` || active == `/${_link}/`))) || (s.selected != undefined && s.selected) || (active === _key)) tab_item_classes.push('cursor');

						return (
							<li key={i} id={`section_tab_${new_i}`} class={tab_classes.join(" ")} style={s.style}>
								{(s?.router ?? false) ? (
									<Link to={link} class={tab_item_classes.join(" ")}>
										<span>{(s?.autotranslate ?? true) ? i18n(s.label) : s.label}</span>
									</Link>
								) : (
									<ActionLink
										_class={{other:tab_item_classes.join(" ")}}
										_href={(link !== false) ? link : null}
										onClick={s?.onClick ?? null}
										autotranslate={s?.autotranslate ?? true}
									>{s.label}</ActionLink>
								)}

								{(s.removable) && (
									<SquaredButton
										icon='ep-delete-circle'
										_class={{other:'remove_filter_tab redIcon gui_small_icon'}}
										onClick={() => removeTab(s.onRemoveTab)}
										aria-label="Remove tab"
									/>
								)}
							</li>
						)
					})}

					{(props.add_menu != undefined) && (
						<li id="section_tab_99" class="overflowed_visible">
							<FloatingSelectorList button_label={props.add_menu.label} _elements={props.add_menu.sections}>
								<MenuItem />
							</FloatingSelectorList>
						</li>
					)}

					{(props.children != undefined) && (
						<li id="section_tab_100" class={'gui_flex_end filter_buttons' + ((props.children_class != undefined) ? ` ${props.children_class}` : '')}>
							{props.children}
						</li>
					)}

					{(add_dom_separators) && (
						<li class="separators_int" />
					)}

					{(add_dom_separators) && (
						<li class="separators_ext" />
					)}
				</ul>
			</div>

			{(display_prev_section) && (
				<div class="more_tabs is_prev">
					<SquaredButton
						icon='ep-prev'
						onClick={() => goSection(-1)}
						aria-label="Go previous section"
					/>
				</div>
			)}

			{(display_next_section) && (
				<div class="more_tabs is_next">
					<SquaredButton
						icon='ep-next'
						onClick={() => goSection(1)}
						aria-label="Go next section"
					/>
				</div>
			)}
		</div>
	);
}

export default injectIntl(NavigationTabs);